import type { VotingPeriod } from "@book-club/shared";
import { orderBy, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functionsService } from "@/infrastructure/firebase/firebase";
import firebaseDatabaseService from "@/infrastructure/firebase/firebaseDatabaseService";
import logger from "@/logger";

type CreateVotingPeriodInput = {
    candidateBookIds: string[];
    endsAt: string; // Date-only string (YYYY-MM-DD)
    startsAt: string; // Date-only string (YYYY-MM-DD)
    targetMonth: number;
    targetYear: number;
};

/**
 * Creates a new voting period via Cloud Function
 * @param input - Voting period input data
 * @returns Created voting period
 */
async function createVotingPeriod(input: CreateVotingPeriodInput): Promise<VotingPeriod> {
    logger.debug(`[Voting] Creating voting period for ${input.targetMonth}/${input.targetYear}`);

    const createVotingPeriodFunction = httpsCallable<CreateVotingPeriodInput, VotingPeriod>(
        functionsService,
        "createVotingPeriod"
    );

    const result = await createVotingPeriodFunction(input);

    logger.debug(`[Voting] Voting period created successfully: ${result.data.id}`);

    // Convert timestamps to Date objects
    const votingPeriod = result.data;
    return {
        ...votingPeriod,
        createdAt: votingPeriod.createdAt instanceof Date ? votingPeriod.createdAt : new Date(votingPeriod.createdAt),
        endsAt: votingPeriod.endsAt instanceof Date ? votingPeriod.endsAt : new Date(votingPeriod.endsAt),
        startsAt: votingPeriod.startsAt instanceof Date ? votingPeriod.startsAt : new Date(votingPeriod.startsAt),
    };
}

/**
 * Fetches all open voting periods from Firestore
 * @returns Array of open voting periods, ordered by creation date (newest first)
 */
async function getVotingPeriods(): Promise<VotingPeriod[]> {
    logger.debug("[Voting] Fetching open voting periods...");

    const votingPeriods = await firebaseDatabaseService.getDocuments<VotingPeriod>(
        "votingPeriods",
        where("status", "==", "open"),
        orderBy("createdAt", "desc")
    );

    logger.debug(`[Voting] Found ${votingPeriods.length} open voting periods`);

    return votingPeriods;
}

type CloseVotingPeriodAndScheduleInput = {
    bookId: string;
    month: number;
    votingPeriodId: string;
    year: number;
};

/**
 * Closes a voting period and schedules the winning book
 * @param input - Input data for closing voting period and scheduling book
 * @returns Updated voting period
 */
async function closeVotingPeriodAndSchedule(input: CloseVotingPeriodAndScheduleInput): Promise<VotingPeriod> {
    logger.debug(
        `[Voting] Closing voting period ${input.votingPeriodId} and scheduling book ${input.bookId} for ${input.month}/${input.year}`
    );

    const closeVotingPeriodAndScheduleFunction = httpsCallable<CloseVotingPeriodAndScheduleInput, VotingPeriod>(
        functionsService,
        "closeVotingPeriodAndSchedule"
    );

    const result = await closeVotingPeriodAndScheduleFunction(input);

    logger.debug(`[Voting] Voting period closed and book scheduled successfully: ${result.data.id}`);

    // Convert timestamps to Date objects
    const votingPeriod = result.data;
    return {
        ...votingPeriod,
        createdAt: votingPeriod.createdAt instanceof Date ? votingPeriod.createdAt : new Date(votingPeriod.createdAt),
        endsAt: votingPeriod.endsAt instanceof Date ? votingPeriod.endsAt : new Date(votingPeriod.endsAt),
        startsAt: votingPeriod.startsAt instanceof Date ? votingPeriod.startsAt : new Date(votingPeriod.startsAt),
    };
}

type UpdateVotingPeriodInput = {
    endsAt: string;
    startsAt: string;
    votingPeriodId: string;
};

async function updateVotingPeriod(input: UpdateVotingPeriodInput): Promise<VotingPeriod> {
    logger.debug(`[Voting] Updating voting period ${input.votingPeriodId}`);

    const updateVotingPeriodFunction = httpsCallable<UpdateVotingPeriodInput, VotingPeriod>(
        functionsService,
        "updateVotingPeriod"
    );

    const result = await updateVotingPeriodFunction(input);

    logger.debug(`[Voting] Voting period updated successfully: ${result.data.id}`);

    const votingPeriod = result.data;
    return {
        ...votingPeriod,
        createdAt: votingPeriod.createdAt instanceof Date ? votingPeriod.createdAt : new Date(votingPeriod.createdAt),
        endsAt: votingPeriod.endsAt instanceof Date ? votingPeriod.endsAt : new Date(votingPeriod.endsAt),
        startsAt: votingPeriod.startsAt instanceof Date ? votingPeriod.startsAt : new Date(votingPeriod.startsAt),
    };
}

type CancelVotingPeriodInput = {
    votingPeriodId: string;
};

async function cancelVotingPeriod(input: CancelVotingPeriodInput): Promise<VotingPeriod> {
    logger.debug(`[Voting] Cancelling voting period ${input.votingPeriodId}`);

    const cancelVotingPeriodFunction = httpsCallable<CancelVotingPeriodInput, VotingPeriod>(
        functionsService,
        "cancelVotingPeriod"
    );

    const result = await cancelVotingPeriodFunction(input);

    logger.debug(`[Voting] Voting period cancelled successfully: ${result.data.id}`);

    const votingPeriod = result.data;
    return {
        ...votingPeriod,
        createdAt: votingPeriod.createdAt instanceof Date ? votingPeriod.createdAt : new Date(votingPeriod.createdAt),
        endsAt: votingPeriod.endsAt instanceof Date ? votingPeriod.endsAt : new Date(votingPeriod.endsAt),
        startsAt: votingPeriod.startsAt instanceof Date ? votingPeriod.startsAt : new Date(votingPeriod.startsAt),
    };
}

const votingService = {
    cancelVotingPeriod,
    closeVotingPeriodAndSchedule,
    createVotingPeriod,
    getVotingPeriods,
    updateVotingPeriod,
};

export default votingService;
